import type { CustomRequirementCondition } from '@onefootprint/request-types/dashboard';
import type { Question } from '../types';

/**
 * Removes references to invalid question ids from a condition.
 * Returns undefined if the entire condition becomes invalid.
 */
const removeInvalidReferences = (
  condition: CustomRequirementCondition | undefined,
  validIdentifiers: Set<string>,
): CustomRequirementCondition | undefined => {
  if (!condition) return undefined;

  if (condition.kind === 'expression') {
    return validIdentifiers.has(condition.field) ? condition : undefined;
  }

  if (condition.kind === 'and' || condition.kind === 'or') {
    const validConditions = condition.conditions
      .map(c => removeInvalidReferences(c, validIdentifiers))
      .filter((c): c is CustomRequirementCondition => c !== undefined);

    if (validConditions.length === 0) {
      return undefined;
    }
    if (validConditions.length === 1) {
      return validConditions[0];
    }
    return {
      ...condition,
      conditions: validConditions,
    };
  }

  return condition;
};

/**
 * Returns the ids of the questions a question at a given index can reference.
 * Only questions before the given index (and not text_blocks) can be referenced.
 */
const getValidIdentifiersForIndex = (questions: Question[], index: number): Set<string> => {
  const validIdentifiers = new Set<string>();
  for (let i = 0; i < index; i++) {
    const q = questions[i];
    if (q.kind !== 'text_block') {
      validIdentifiers.add(q.id);
    }
  }
  return validIdentifiers;
};

export type ConditionUpdate = {
  index: number;
  cleanedCondition: CustomRequirementCondition | undefined;
};

/**
 * Computes condition updates needed after questions change (deletion or reorder).
 * Returns an array of updates for questions whose conditions reference invalid question ids.
 */
export const getConditionUpdates = (questions: Question[], excludeIdentifiers?: Set<string>): ConditionUpdate[] => {
  const updates: ConditionUpdate[] = [];

  questions.forEach((question, idx) => {
    if (question.condition) {
      const validIdentifiers = getValidIdentifiersForIndex(questions, idx);
      if (excludeIdentifiers) {
        excludeIdentifiers.forEach(id => {
          validIdentifiers.delete(id);
        });
      }
      const cleanedCondition = removeInvalidReferences(question.condition, validIdentifiers);
      if (cleanedCondition !== question.condition) {
        updates.push({ index: idx, cleanedCondition });
      }
    }
  });

  return updates;
};
